import { useMemo } from 'react';

import type {
  MaintenanceListTableData,
  MaintenanceListTableDisplay,
  MaintenanceListTableGroupItem,
} from '@/components/sections/service/maintenance-list/table';
import { MaintenanceListTableBriefBody } from '@/components/sections/service/maintenance-list/table/brief/body';
import { TableBriefHeaderNew } from '@/components/sections/service/maintenance-list/table/brief/header-new';
import { MaintenanceListTableBriefImageTitle } from '@/components/sections/service/maintenance-list/table/brief/image-title';
import { ButtonGroup } from '@/components/shared/buttons/button-group';
import { Box } from '@/components/shared/layout/box';
import type { RegularMaintenaceSchedule } from '@/types/services/contentstack/services/maintenance-service';
import type { IButton } from '@/types/shared';
import type { ITest, ITracking } from '@/types/tracking';

type DealerSearchButton = {
  button: IButton;
  tracking: ITracking;
  test: ITest;
};

export interface MaintenanceListTableBriefData {
  vehicleImageSrc?: string;
  dealerSearchButton?: DealerSearchButton;
  onChangeSchedule?: (schedule: RegularMaintenaceSchedule) => void;
}

interface MaintenanceListTableBriefProps
  extends MaintenanceListTableBriefData,
    MaintenanceListTableData {
  tableItems: MaintenanceListTableGroupItem[];
  tableDisplay?: MaintenanceListTableDisplay;
}

const MAX_MONTH_BADGE_DISPLAY = 36;
const BADGE_TEXT = 'ในระยะรับประกันรถ';

export const MaintenanceListTableBrief = ({
  maintenanceSchedules,
  selectedIndex = 0,
  selectedItem,
  tableDisplay,
  vehicleType = 'lcv',
  vehicleImageSrc = '',
  tableItems,
  priceData,
  dealerSearchButton,
  onChangeSchedule,
}: MaintenanceListTableBriefProps) => {
  const selectedMaintenanceSchedule = useMemo(
    () => maintenanceSchedules?.[selectedIndex],
    [maintenanceSchedules, selectedIndex]
  );
  const isEuro5 = useMemo(
    () =>
      `${selectedItem?.european_emission_standard}`
        .replaceAll(/\s/g, '')
        .toLowerCase() === 'euro5',
    [selectedItem]
  );
  return (
    <Box backgroundColor="white" paddingTop={{ '@initial': 2, '@lg': 4 }}>
      <TableBriefHeaderNew
        dataTest="maintenace_table_brief_header"
        selectedIndex={selectedIndex}
        selectedMaintenanceSchedule={selectedMaintenanceSchedule}
        maintenanceSchedules={maintenanceSchedules}
        tableDisplay={tableDisplay}
        onChangeSchedule={onChangeSchedule}
      />
      <Box backgroundColor="white" padding={{ '@lg': 7 }}>
        <MaintenanceListTableBriefImageTitle
          showBadge={
            vehicleType === 'lcv' &&
            !!selectedMaintenanceSchedule &&
            selectedMaintenanceSchedule.month <= MAX_MONTH_BADGE_DISPLAY
          }
          badgeText={BADGE_TEXT}
          imageSrc={vehicleImageSrc}
          showIconEuro5Max={isEuro5}
        />
        <MaintenanceListTableBriefBody
          vehicleType={vehicleType}
          selectedIndex={selectedIndex}
          selectedMaintenanceSchedule={selectedMaintenanceSchedule}
          tableItems={tableItems}
          priceData={priceData}
        />
        {!!dealerSearchButton && (
          <ButtonGroup
            justify="center"
            css={{
              py: '$space-8',
              '@md': {
                py: '$space-12',
              },
            }}
            variant="button-gray"
            buttons={[dealerSearchButton.button]}
            tracking={dealerSearchButton.tracking}
            test={dealerSearchButton.test}
          />
        )}
      </Box>
    </Box>
  );
};
